import { useMemo } from 'react';
import BackgroundDots from '../BackgroundDots';
import useLanguage from '../../../hooks/useLanguage';
import { getLegalContent } from '../../legal/legalContent';
import { LEGAL_PAGES, getLegalPath } from '../../legal/legalRoutes';
import '../LoopinoDesktopGuidelines.css';
import './DesktopLegalPage.css';

const DesktopLegalPage = ({ pageKey, onNavigate }) => {
  const { language, t } = useLanguage();
  const content = useMemo(() => getLegalContent(language, pageKey), [language, pageKey]);

  const navItems = useMemo(() => (
    LEGAL_PAGES.map((page) => ({
      key: page.key,
      path: page.path,
      label: t(`legal.nav.${page.key}`, {
        defaultValue: page.key === 'terms' ? 'Terms of Service' : page.key === 'cookies' ? 'Cookie Policy' : page.key === 'notice' ? 'Legal Notice' : 'Privacy Policy'
      })
    }))
  ), [t]);

  const handleNavClick = (key) => {
    if (key === pageKey) {
      return;
    }
    if (typeof onNavigate === 'function') {
      onNavigate(key);
      return;
    }
    const target = getLegalPath(key);
    if (target && typeof window !== 'undefined') {
      window.location.href = target;
    }
  };

  const handleBack = () => {
    if (typeof window === 'undefined') return;
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
    window.location.href = '/';
  };

  return (
    <div className="desktop-legal-layer">
      <BackgroundDots className="desktop-legal-background" />
      <div className="desktop-legal-shell">
        <div className="desktop-legal-container">
          <header className="desktop-legal-header">
            <div className="desktop-legal-header-copy">
              <p className="loopino-eyebrow">
                {t('legal.eyebrow', { defaultValue: 'Loopino legal' })}
              </p>
              <h1 className="desktop-legal-title">{content.title}</h1>
              <p className="desktop-legal-summary">{content.summary}</p>
            </div>
            <div className="desktop-legal-actions">
              <button
                type="button"
                className="loopino-button ghost"
                onClick={handleBack}
              >
                {t('legal.actions.back', { defaultValue: 'Back' })}
              </button>
              <a className="loopino-button primary" href="/">
                {t('legal.actions.app', { defaultValue: 'Open app' })}
              </a>
            </div>
          </header>

          <nav className="desktop-legal-nav" aria-label={t('legal.nav.title', { defaultValue: 'Legal documents' })}>
            {navItems.map((item) => (
              <button
                key={item.key}
                type="button"
                className={`loopino-button surface desktop-legal-nav-button ${item.key === pageKey ? 'is-active' : ''}`}
                onClick={() => handleNavClick(item.key)}
                aria-current={item.key === pageKey ? 'page' : undefined}
              >
                {item.label}
              </button>
            ))}
          </nav>

          <article className="loopino-card desktop-legal-card">
            <div className="desktop-legal-meta">
              <span className="desktop-legal-updated">
                {t('legal.updated', { defaultValue: 'Last updated: {{date}}', date: content.updated })}
              </span>
            </div>

            <div className="desktop-legal-sections">
              {content.sections.map((section, index) => (
                <section key={`${section.title}-${index}`} className="desktop-legal-section">
                  <h2>{section.title}</h2>
                  {section.paragraphs?.map((paragraph, paragraphIndex) => (
                    <p key={`${section.title}-p-${paragraphIndex}`}>{paragraph}</p>
                  ))}
                  {section.bullets?.length ? (
                    <ul>
                      {section.bullets.map((bullet, bulletIndex) => (
                        <li key={`${section.title}-b-${bulletIndex}`}>{bullet}</li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ))}
            </div>
          </article>
        </div>
      </div>
    </div>
  );
};

export default DesktopLegalPage;
